import { useMutation } from "@apollo/client"
import React from "react"
import { useTranslation } from "react-i18next"
import { useRecoilState } from "recoil"
import { DELETE_PROJECT } from "../../api/QuadriApi"
import { addSnackSelector } from "../StateSelectors"
import { ConfigStatus, startBackupTimeoutReload } from "./ConfigMain"
import DeleteModal from "./DeleteModal"
import { track } from "../../util/gainsight"
import Modal from "../Modal"
import { AccountAdmin } from "../../types"

interface QuadriFooterProps {
  projectStatus: ConfigStatus | undefined
  changeInProgress: boolean
  connectProjectId: string
  isAllowedToEnableAndRemoveExtensionbyPolicy: boolean
  extensionOwnershipName: string | undefined
  accountAdmins: AccountAdmin[]
  isTcpsLicensed: boolean
  isRemoveButtonHidden: boolean
}
//
const QuadriFooter = (props: QuadriFooterProps) => {
  const { connectProjectId, projectStatus } = props
  const { t } = useTranslation("config")

  const [, addSnack] = useRecoilState(addSnackSelector)
  const [disabled, setDisabled] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  const [showMessage, setShowMessage] = React.useState<boolean>(false)
  const showModal = () => setShowDeleteModal(true)
  const closeModal = () => setShowDeleteModal(false)
  const showMessageModal = () => setShowMessage(true)
  const closeMessageModal = () => setShowMessage(false)
  const [showAllAdmins, setShowAllAdmins] = React.useState(false)
  const displayedAdmins = showAllAdmins ? props.accountAdmins : props.accountAdmins.slice(0, 10)

  // Mutation used for disabling after the project has been created
  const [deleteProjectMutation] = useMutation(DELETE_PROJECT, {
    onCompleted: (data: any) => startBackupTimeoutReload(t, addSnack)
  })

  const deleteProject = (purge: boolean) => {
    track("project_delete", { connectProjectId: connectProjectId })
    setDeleting(true)
    deleteProjectMutation({
      variables: {
        deleteProjectRequest: {
          connectProjectId,
          purge,
          isTcpsLicensed: props.isTcpsLicensed
        }
      }
    })
  }

  const isDeleting = deleting || projectStatus === "Deleting"

  // const buttonDeleteProject = !deleting ? deleteProject : undefined
  const buttonDeleteProject = !isDeleting
    ? props.isAllowedToEnableAndRemoveExtensionbyPolicy
      ? showModal
      : showMessageModal
    : undefined

  return (
    <div className="row mb-5">
      {showMessage && (
        <Modal heading={t("extensionPolicy.heading")} closeModal={closeMessageModal} large={true}>
          <div>{t("extensionPolicy.remove") + props.extensionOwnershipName + t("extensionPolicy.commonText")}</div>
          <br />
          <ul>
            {displayedAdmins.map((admin: AccountAdmin, index: number) => (
              <li key={index}>
                <strong>{admin.fullName}</strong> ({admin.email})
              </li>
            ))}
          </ul>
          {/* Show "Show More" or "Show Less" link based on admin count */}
          {props.accountAdmins.length > 10 && !showAllAdmins && (
            <button
              className="m-0 p-0"
              onClick={() => setShowAllAdmins(true)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#005F9E" }}
            >
              {t("extensionPolicy.showMore")}
            </button>
          )}
          {showAllAdmins && (
            <button
              onClick={() => setShowAllAdmins(false)}
              className="m-0 p-0"
              style={{ background: "none", border: "none", cursor: "pointer", color: "#005F9E" }}
            >
              {t("extensionPolicy.showLess")}
            </button>
          )}
        </Modal>
      )}
      {showDeleteModal && <DeleteModal closeModal={closeModal} deleteProject={deleteProject} />}
      <div className="col-12 head-container mt-0">
        <div className="page-heading mt-0">
          <div />
          {/* This is the case for when a project is not active */}
          {typeof projectStatus === "undefined" ? (
            <button className="button warning ml-1" type="button" disabled={true}>
              {t("quadriFooter.disable")}
            </button>
          ) : (
            // This is the case for when a project is active, but in some state
            !props.isRemoveButtonHidden && (
              <button
                className="button warning ml-1"
                type="button"
                onClick={buttonDeleteProject}
                disabled={props.changeInProgress || isDeleting}
                data-testid="quadri-open-delete-project-button"
              >
                {(isDeleting || disabled) && <i className="icon-font tc-icon-spinner-light"></i>}
                {isDeleting
                  ? t("quadriFooter.disablingQuadri")
                  : disabled
                    ? t("quadriFooter.quadriDisabled")
                    : t("quadriFooter.disable")}
              </button>
            )
          )}
          {/* {projectStatus !== "Deleting" ? (
            <button className="button warning ml-1" type="button" disabled={true}>
              Disable
            </button>
          ) : (
            <button className="button warning ml-1" type="button" onClick={buttonDeleteProject}>
              {(isDeleting || disabled) && <i className="icon-font tc-icon-spinner-light"></i>}
              {isDeleting ? "Disabling Quadri..." : disabled ? "Disabled, reloading page" : "Disable"}
            </button>
          )} */}
        </div>
      </div>
    </div>
  )
}

export default QuadriFooter
